/**
 * Dynamic API configuration for Studio
 * Automatically detects environment and uses appropriate endpoints
 */

const getApiBaseUrl = () => {
  // Prefer explicit override so deploys can pin an environment
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // During browser runtime, peek at host to decide whether we are local
  if (typeof window !== 'undefined') {
    const { hostname } = window.location;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    if (isLocalhost) {
      return 'http://localhost:8000';
    }
  }

  return 'https://studio-api.loafstack.dev';
};

export const API_BASE_URL = getApiBaseUrl();

/**
 * Enhanced fetch wrapper with error handling and retry logic
 */
export const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;

  const method = (options.method || 'GET').toUpperCase();
  const headers = new Headers(options.headers || {});
  const hasBody = options.body !== undefined && options.body !== null;
  const bodyIsFormLike =
    typeof FormData !== 'undefined' && options.body instanceof FormData;

  if (!headers.has('Content-Type') && hasBody && !bodyIsFormLike) {
    headers.set('Content-Type', 'application/json');
  }

  const requestInit = {
    ...options,
    method,
    headers
  };

  try {
    const response = await fetch(url, requestInit);

    if (!response.ok) {
      const error = new Error(`API Error: ${response.status} ${response.statusText}`);
      error.status = response.status;
      error.statusText = response.statusText;
      error.url = url;
      try {
        const bodyText = await response.text();
        if (bodyText) {
          error.body = bodyText;
          try {
            error.data = JSON.parse(bodyText);
          } catch (_ignore) {}
        }
      } catch (_readErr) {}
      throw error;
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }

    return await response.text();
  } catch (error) {
    if (error?.status !== 404) {
      console.error(`API request failed for ${url}:`, error);
    }
    throw error;
  }
};

/**
 * Specific API endpoints
 */
const prompts = {};
prompts.list = () => apiRequest('/lock/prompts');
prompts.get = (key) => apiRequest(`/lock/prompts/${key}`);
prompts.diff = (baselineId, candidateId) => apiRequest(`/lock/versions/diff?baseline=${baselineId}&candidate=${candidateId}`);
prompts.versions = async (key) => {
  const detail = await prompts.get(key);
  return Array.isArray(detail?.versions) ? detail.versions : [];
};
prompts.createVersion = (key, payload) => apiRequest(`/lock/prompts/${encodeURIComponent(key)}/versions`, {
  method: 'POST',
  body: JSON.stringify(payload)
});
prompts.submitVersion = (versionId) => apiRequest(`/lock/versions/${versionId}/submit`, {
  method: 'POST'
});
prompts.approveVersion = (versionId, stage = 'review') => apiRequest(`/lock/versions/${versionId}/approve${stage ? `?stage=${stage}` : ''}`, {
  method: 'POST'
});
prompts.allVersions = async () => {
  const summaries = await prompts.list();
  if (!Array.isArray(summaries) || summaries.length === 0) {
    return [];
  }

  const detailPromises = summaries.map(async (prompt) => {
    try {
      const versions = await prompts.versions(prompt.key);
      return versions.map((version) => ({
        ...version,
        promptKey: prompt.key,
        label: `${prompt.key} v${version.version}`
      }));
    } catch (error) {
      console.error('Failed to fetch prompt versions', prompt.key, error);
      return [];
    }
  });

  const results = await Promise.all(detailPromises);
  return results.flat();
};

const proof = {};
proof.suites = () => apiRequest('/proof/suites');
proof.createSuite = (suite) => apiRequest('/proof/suites', {
  method: 'POST',
  body: JSON.stringify(suite)
});
proof.runs = () => apiRequest('/proof/runs');
proof.run = (runId) => apiRequest(`/proof/runs/${runId}`);
proof.cases = (runId) => apiRequest(`/proof/runs/${runId}/cases`);
proof.createRun = (suiteId, config) => apiRequest(`/proof/run?suite_id=${suiteId}`, {
  method: 'POST',
  body: JSON.stringify(config)
});
proof.streamRun = (versionId, suiteId) => `${API_BASE_URL}/proof/run.stream?version_id=${versionId}&suite_id=${suiteId}`;
proof.slices = (runId) => apiRequest(`/proof/runs/${runId}/slices`);

const admin = {};
admin.seed = () => apiRequest('/admin/seed', {
  method: 'POST'
});

const agents = {};
agents.jobsForRun = (runId) => apiRequest(`/agents/runs/${runId}`);
agents.recommendations = (limit = 5) => apiRequest(`/agents/recommendations?limit=${limit}`);

const rag = {};
rag.createDocument = (doc) => apiRequest('/rag/documents', {
  method: 'POST',
  body: JSON.stringify(doc)
});
rag.listDocuments = ({ collection, limit = 20, offset = 0, search } = {}) => {
  const params = new URLSearchParams();
  if (collection) params.set('collection', collection);
  params.set('limit', limit);
  params.set('offset', offset);
  if (search) params.set('search', search);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return apiRequest(`/rag/documents${suffix}`);
};
rag.deleteDocument = (documentId) => apiRequest(`/rag/documents/${documentId}`, {
  method: 'DELETE'
});
rag.search = (payload) => apiRequest('/rag/search', {
  method: 'POST',
  body: JSON.stringify(payload)
});

export const API = {
  health: () => apiRequest('/health'),
  prompts,
  proof,
  admin,
  agents,
  rag,
  system: {
    health: () => apiRequest('/system/health'),
    timeline: (limit = 100) => apiRequest(`/system/timeline?limit=${limit}`),
    recordTimeline: (payload) => apiRequest('/system/timeline', {
      method: 'POST',
      body: JSON.stringify(payload)
    })
  }
};

/**
 * Legacy exports for backwards compatibility
 */
export const apiFetch = (path, opts = {}) => fetch(`${API_BASE_URL}${path}`, opts);

/**
 * Environment info for debugging
 */
export const ENV_INFO = {
  isDevelopment: process.env.NODE_ENV === 'development',
  apiUrl: API_BASE_URL,
  hostname: window.location.hostname,
  environment: API_BASE_URL.includes('loafstack.dev') ? 'production' : 'development'
};

// Log environment info in development
if (ENV_INFO.isDevelopment) {
  console.log('Studio API Configuration:', ENV_INFO);
}
